/**
 * Msg 573 · Phase I.5 — Bulk attribute manager.
 *
 * Admin-only page mounted at `/atlas/admin/bulk-attributes` that lets
 * an admin filter `atlas_facts` (attribute-typed) rows across many
 * topics and apply one of three bulk actions:
 *   • Delete N
 *   • Rename attribute key
 *   • Move to child folder (optionally with a key rename during move)
 */
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  Search as SearchIcon, Loader2, Trash2, Pencil, MoveRight,
  ArrowLeft, AlertTriangle,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";


const ENTITY_TYPES = [
  "", "destination", "cruise_line", "cruise_line_group",
  "cruise_ship", "resort", "hotel", "port", "region", "other",
];


// Msg 575 — when this page is embedded under `/atlas/topics` (the
// consolidated Topics tab), "Back" flips the segmented view instead of
// navigating away.
function useBulkAdminNav() {
  const nav = useNavigate();
  const loc = useLocation();
  const [sp, setSp] = useSearchParams();
  const embedded = loc.pathname.startsWith("/atlas/topics");
  return {
    embedded,
    goTemplates: () => {
      if (embedded) {
        const next = new URLSearchParams(sp);
        next.set("view", "templates");
        next.delete("open");
        setSp(next);
      } else {
        nav("/atlas/admin/templates");
      }
    },
  };
}


export default function AtlasBulkAttributesPage() {
  const admNav = useBulkAdminNav();
  const [attributeKey, setAttributeKey] = useState("");
  const [exactMatch, setExactMatch] = useState(true);
  const [topicSlugPattern, setTopicSlugPattern] = useState("");
  const [entityType, setEntityType] = useState("");
  const [rows, setRows] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [action, setAction] = useState("");             // '' | delete | rename_key | move_to_child_folder
  const [newKey, setNewKey] = useState("");
  const [childFolderName, setChildFolderName] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const runSearch = useCallback(async () => {
    setSearching(true); setSelected(new Set());
    try {
      const params = new URLSearchParams();
      if (attributeKey) params.set("attribute_key", attributeKey);
      params.set("attribute_key_exact", exactMatch ? "true" : "false");
      if (topicSlugPattern) params.set("topic_slug_pattern", topicSlugPattern);
      if (entityType) params.set("entity_type", entityType);
      const r = await api.get(`/atlas/admin/bulk-attributes/search?${params.toString()}`);
      setRows(r.data?.facts || []);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex?.response?.data?.detail) || "Search failed.");
    } finally { setSearching(false); }
  }, [attributeKey, exactMatch, topicSlugPattern, entityType]);

  const allSelected = rows.length > 0 && selected.size === rows.length;
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectedRows = useMemo(
    () => rows.filter((r) => selected.has(r.id)),
    [rows, selected],
  );

  const canApply = selectedRows.length > 0 && (
    action === "delete" ||
    (action === "rename_key" && newKey.trim().length > 0) ||
    (action === "move_to_child_folder" && childFolderName.trim().length > 0)
  );

  const doApply = async () => {
    if (!canApply) return;
    setBusy(true); setConfirmOpen(false);
    try {
      const body = {
        action,
        fact_ids: Array.from(selected),
      };
      if (action === "rename_key") body.new_key = newKey.trim();
      if (action === "move_to_child_folder") {
        body.child_folder_name = childFolderName.trim();
        if (newKey.trim()) body.new_key = newKey.trim();
      }
      const r = await api.post("/atlas/admin/bulk-attributes/action", body);
      toast.success(`Action applied to ${r.data?.touched} fact(s).`);
      // Refresh
      await runSearch();
      setAction(""); setNewKey(""); setChildFolderName("");
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex?.response?.data?.detail) || "Action failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl" data-testid="atlas-bulk-attributes-page">
      <div className="flex items-center gap-2 mb-2">
        <button type="button" onClick={() => admNav.goTemplates()}
          data-testid="atlas-bulk-attr-back"
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-tb-navy/70 hover:bg-tb-navy/5 font-hand text-xs">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
      </div>
      <div className="font-hand text-[11px] uppercase tracking-widest text-tb-navy/50">
        Atlas Admin · Bulk tools
      </div>
      <h1 className="mt-1 text-tb-navy text-3xl md:text-4xl font-semibold"
          style={{ fontFamily: '"Playfair Display", serif' }}>
        Bulk attribute manager
      </h1>
      <p className="mt-1 font-hand text-tb-navy/60 text-sm max-w-2xl">
        Filter existing structured attributes across the whole topic tree, then
        delete, rename, or move them under a child folder in one click.
        Destructive actions ask for confirmation and count the rows they'll
        touch before running.
      </p>

      <FilterCard
        attributeKey={attributeKey} setAttributeKey={setAttributeKey}
        exactMatch={exactMatch} setExactMatch={setExactMatch}
        topicSlugPattern={topicSlugPattern} setTopicSlugPattern={setTopicSlugPattern}
        entityType={entityType} setEntityType={setEntityType}
        searching={searching} onSearch={runSearch}
      />

      <ResultTable rows={rows} selected={selected}
        allSelected={allSelected}
        onToggleAll={toggleAll} onToggleOne={toggleOne} />

      {selectedRows.length > 0 && (
        <ActionBar
          count={selectedRows.length}
          action={action} setAction={setAction}
          newKey={newKey} setNewKey={setNewKey}
          childFolderName={childFolderName} setChildFolderName={setChildFolderName}
          canApply={canApply}
          onApply={() => setConfirmOpen(true)}
          busy={busy}
        />
      )}

      {confirmOpen && (
        <ConfirmDialog
          action={action}
          count={selectedRows.length}
          childFolderName={childFolderName}
          newKey={newKey}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={doApply}
          busy={busy}
        />
      )}
    </div>
  );
}


function FilterCard({ attributeKey, setAttributeKey, exactMatch, setExactMatch,
                     topicSlugPattern, setTopicSlugPattern,
                     entityType, setEntityType, searching, onSearch }) {
  return (
    <div className="mt-5 rounded-2xl border border-tb-navy/15 bg-white p-4"
         data-testid="atlas-bulk-attr-filters">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <label className="block">
          <span className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">Attribute key</span>
          <input value={attributeKey}
            onChange={(e) => setAttributeKey(e.target.value)}
            data-testid="atlas-bulk-attr-key"
            placeholder="e.g. casino_size"
            className="mt-1 w-full h-10 px-3 rounded-lg border border-tb-navy/25 outline-none font-hand text-sm bg-white text-tb-navy" />
        </label>
        <label className="block">
          <span className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">Match</span>
          <select value={exactMatch ? "exact" : "sub"}
            onChange={(e) => setExactMatch(e.target.value === "exact")}
            data-testid="atlas-bulk-attr-match"
            className="mt-1 w-full h-10 px-3 rounded-lg border border-tb-navy/25 bg-white font-hand text-sm text-tb-navy">
            <option value="exact">Exact</option>
            <option value="sub">Substring</option>
          </select>
        </label>
        <label className="block">
          <span className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">Topic slug contains</span>
          <input value={topicSlugPattern}
            onChange={(e) => setTopicSlugPattern(e.target.value)}
            data-testid="atlas-bulk-attr-slug"
            placeholder="e.g. carnival"
            className="mt-1 w-full h-10 px-3 rounded-lg border border-tb-navy/25 outline-none font-hand text-sm bg-white text-tb-navy" />
        </label>
        <label className="block">
          <span className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">Folder Type</span>
          <select value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
            data-testid="atlas-bulk-attr-entity"
            className="mt-1 w-full h-10 px-3 rounded-lg border border-tb-navy/25 bg-white font-hand text-sm text-tb-navy">
            {ENTITY_TYPES.map((t) => (
              <option key={t || "any"} value={t}>{t || "any"}</option>
            ))}
          </select>
        </label>
      </div>
      <div className="mt-3 flex items-center justify-end">
        <button type="button" onClick={onSearch} disabled={searching}
          data-testid="atlas-bulk-attr-search-btn"
          className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm disabled:opacity-50">
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <SearchIcon className="w-4 h-4" />}
          Search
        </button>
      </div>
    </div>
  );
}


function ResultTable({ rows, selected, allSelected, onToggleAll, onToggleOne }) {
  if (rows.length === 0) {
    return (
      <p className="mt-4 font-hand text-tb-navy/50 text-sm"
         data-testid="atlas-bulk-attr-empty">
        No results. Enter filters above and press Search.
      </p>
    );
  }
  return (
    <div className="mt-4 overflow-hidden rounded-2xl border border-tb-navy/15 bg-white"
         data-testid="atlas-bulk-attr-results">
      <div className="flex items-center justify-between px-3 py-2 border-b border-tb-navy/10">
        <span className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">
          {rows.length} result{rows.length === 1 ? "" : "s"}
        </span>
        <label className="inline-flex items-center gap-2 font-hand text-xs text-tb-navy/70 cursor-pointer">
          <input type="checkbox" checked={allSelected} onChange={onToggleAll}
            data-testid="atlas-bulk-attr-toggle-all" />
          Select all
        </label>
      </div>
      <table className="w-full">
        <thead className="bg-tb-navy/[0.03]">
          <tr>
            <th className="w-10 px-3 py-2"></th>
            <th className="text-left px-3 py-2 font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">Key</th>
            <th className="text-left px-3 py-2 font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">Value</th>
            <th className="text-left px-3 py-2 font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">Topic</th>
            <th className="text-left px-3 py-2 font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">Entity</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-tb-navy/10"
                data-testid={`atlas-bulk-attr-row-${r.id}`}>
              <td className="px-3 py-2">
                <input type="checkbox" checked={selected.has(r.id)}
                  onChange={() => onToggleOne(r.id)}
                  data-testid={`atlas-bulk-attr-check-${r.id}`} />
              </td>
              <td className="px-3 py-2 font-hand text-xs text-tb-navy">
                <code>{r.attribute_key}</code>
              </td>
              <td className="px-3 py-2 font-hand text-xs text-tb-navy/80 max-w-md truncate">
                {r.attribute_value}
              </td>
              <td className="px-3 py-2 font-hand text-xs text-tb-navy/70">
                <div><code>{(r.topic_slugs || [])[0] || ""}</code></div>
                <div className="text-tb-navy/40">{r._topic?.name || ""}</div>
              </td>
              <td className="px-3 py-2 font-hand text-xs text-tb-navy/60">
                {r._topic?.entity_type || ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function ActionBar({ count, action, setAction, newKey, setNewKey,
                     childFolderName, setChildFolderName,
                     canApply, onApply, busy }) {
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-20 rounded-2xl bg-tb-navy text-white shadow-2xl border border-tb-gold-bright/40 px-4 py-3 flex flex-wrap items-center gap-3"
         data-testid="atlas-bulk-attr-action-bar">
      <span className="font-hand text-sm">
        <b className="text-tb-gold-bright">{count}</b> selected
      </span>
      <select value={action} onChange={(e) => setAction(e.target.value)}
        data-testid="atlas-bulk-attr-action-select"
        className="h-9 px-2 rounded border border-white/30 bg-tb-navy text-white font-hand text-sm">
        <option value="">Pick action…</option>
        <option value="delete">Delete</option>
        <option value="rename_key">Rename key</option>
        <option value="move_to_child_folder">Move to child folder</option>
      </select>
      {action === "rename_key" && (
        <input value={newKey} onChange={(e) => setNewKey(e.target.value)}
          data-testid="atlas-bulk-attr-new-key"
          placeholder="new_key"
          className="h-9 px-2 rounded border border-white/30 bg-tb-navy text-white placeholder-white/40 font-hand text-sm" />
      )}
      {action === "move_to_child_folder" && (
        <>
          <input value={childFolderName} onChange={(e) => setChildFolderName(e.target.value)}
            data-testid="atlas-bulk-attr-child-folder"
            placeholder="Child folder name"
            className="h-9 px-2 rounded border border-white/30 bg-tb-navy text-white placeholder-white/40 font-hand text-sm" />
          <input value={newKey} onChange={(e) => setNewKey(e.target.value)}
            data-testid="atlas-bulk-attr-move-newkey"
            placeholder="new_key (optional)"
            className="h-9 px-2 rounded border border-white/30 bg-tb-navy text-white placeholder-white/40 font-hand text-sm" />
        </>
      )}
      <button type="button" onClick={onApply} disabled={!canApply || busy}
        data-testid="atlas-bulk-attr-apply"
        className="inline-flex items-center gap-1.5 h-9 px-5 rounded-full bg-tb-gold-bright text-tb-navy font-hand text-sm disabled:opacity-40">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" />
              : action === "delete" ? <Trash2 className="w-4 h-4" />
              : action === "rename_key" ? <Pencil className="w-4 h-4" />
              : <MoveRight className="w-4 h-4" />}
        Apply
      </button>
    </div>
  );
}


function ConfirmDialog({ action, count, childFolderName, newKey,
                          onCancel, onConfirm, busy }) {
  const summary =
    action === "delete"
      ? `Delete ${count} attribute fact${count === 1 ? "" : "s"}?`
      : action === "rename_key"
        ? `Rename ${count} fact key${count === 1 ? "" : "s"} → "${newKey}"?`
        : `Move ${count} fact${count === 1 ? "" : "s"} to child folder "${childFolderName}"${newKey ? ` and rename key → "${newKey}"` : ""}?`;
  return (
    <div className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center p-4"
         data-testid="atlas-bulk-attr-confirm"
         onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl border border-tb-navy/15 p-5">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <h2 className="text-tb-navy text-lg font-semibold"
              style={{ fontFamily: '"Playfair Display", serif' }}>
            Confirm bulk action
          </h2>
        </div>
        <p className="font-hand text-tb-navy/80 text-sm">
          {summary}
        </p>
        <p className="mt-2 font-hand text-tb-navy/50 text-xs">
          This action is not reversible from this UI (recovered from audit
          logs only). Proceed carefully.
        </p>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button type="button" onClick={onCancel}
            data-testid="atlas-bulk-attr-confirm-cancel"
            className="h-9 px-4 rounded-full border border-tb-navy/20 text-tb-navy font-hand text-sm">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} disabled={busy}
            data-testid="atlas-bulk-attr-confirm-apply"
            className="inline-flex items-center gap-1.5 h-9 px-5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Yes, apply"}
          </button>
        </div>
      </div>
    </div>
  );
}
